import React from 'react';
import Person from '@gravity-ui/icons/Person';
import Persons from '@gravity-ui/icons/Persons';
import { RadioButton } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { Field } from '../Field/Field';
import { Channel } from 'client/model/skill';
import { SkillAccess, skillAccesses } from 'client/model/settings';
import { skillLabels } from 'client/blocks/Settings/channels/labels';
import { useCurrentAliceApp } from 'client/hooks/useCurrentAliceApp';
import * as keyset from 'i18n/SettingsLabels';
import { useIsDraft } from 'client/hooks/useIsDraft';

import styles from './InstallableAppAccessField.module.scss';

const t = i18n(keyset);

interface Props {
    installableAppAccess: SkillAccess;
    setInstallableAppAccess: (skillAccess: SkillAccess) => void;
    disabled?: boolean;
    channel: Channel;
}

export const tvAppAccessDescriptions: Record<SkillAccess, string> = {
    [SkillAccess.Hidden]: t('description-installable-app-access-hidden'),
    [SkillAccess.Private]: t('description-installable-app-access-private', { channel: 'TV' }),
    [SkillAccess.Public]: t('description-installable-app-access-public', { channel: 'TV' }),
};

export const smartScreenAppAccessDescriptions: Record<SkillAccess, string> = {
    [SkillAccess.Hidden]: t('description-installable-app-access-hidden'),
    [SkillAccess.Private]: t('description-installable-app-access-private', { channel: 'Duo' }),
    [SkillAccess.Public]: t('description-installable-app-access-public', { channel: 'Duo' }),
};

const getDescription = (channel = Channel.TvApp) => {
    if (channel === Channel.SmartScreenApp) return smartScreenAppAccessDescriptions;
    if (channel === Channel.TvApp) return tvAppAccessDescriptions;
    console.error('@getDescription Error: unsupported channel');
    return {
        [SkillAccess.Hidden]: '',
        [SkillAccess.Private]: '',
        [SkillAccess.Public]: '',
    };
};

const mapInstallableAppAccessToIcon: Record<SkillAccess, React.ReactNode> = {
    [SkillAccess.Hidden]: '',
    [SkillAccess.Private]: <Person />,
    [SkillAccess.Public]: <Persons />,
};

export const InstallableAppAccessField: React.FC<Props> = ({
    installableAppAccess,
    setInstallableAppAccess,
    disabled,
    channel,
}) => {
    const installableApp = useCurrentAliceApp()!;
    const isDraft = useIsDraft(installableApp.channel, installableApp.id);

    const isHiddenAccess = isDraft
        ? installableApp.draft.skillAccess === SkillAccess.Hidden
        : installableApp.skillAccess === SkillAccess.Hidden;

    const displayedInstallableAppAccesses = isHiddenAccess
        ? skillAccesses[channel]
        : skillAccesses[channel]
              .filter((access) => access.type !== SkillAccess.Hidden && access.label)
              .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <Field
            fieldTitle={skillLabels.skillAccess}
            fieldDescription={getDescription(installableApp.channel)[installableAppAccess]}
        >
            <RadioButton
                className={styles.installableAppAccessRadioButtonsContainer}
                disabled={disabled}
                options={displayedInstallableAppAccesses.map(({ type, label }) => ({
                    value: type,
                    content: (
                        <div className={styles.installableAppAccessLabelContainer}>
                            {mapInstallableAppAccessToIcon[type]} {label}
                        </div>
                    ),
                }))}
                onUpdate={(value) => {
                    setInstallableAppAccess(value);
                }}
                value={installableAppAccess}
                size="xl"
            />
        </Field>
    );
};
